import { useEffect, useState, useCallback, useRef } from 'react'
import { useAuth } from '../../context/AuthContext'
import { supabase } from '../../supabaseClient'
import {
  Plus, X, Trash2, Download, ChevronDown,
  FileText, Edit2, Sparkles,
} from 'lucide-react'
import jsPDF from 'jspdf'

const fmt = (n) =>
  new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n || 0)

const fmtDate = (d) =>
  new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' })

// jsPDF-safe number format (avoids euro sign encoding issues)
const fmtPdf = (n) =>
  Number(n || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' EUR'

const STATUS_CFG = {
  bozza:               { label: 'Bozza',              bg: 'bg-gray-500/10',   text: 'text-gray-400',   border: 'border-gray-500/20' },
  inviato:             { label: 'Inviato',             bg: 'bg-blue-500/10',   text: 'text-blue-400',   border: 'border-blue-500/20' },
  accettato:           { label: 'Accettato',           bg: 'bg-green-500/10',  text: 'text-green-400',  border: 'border-green-500/20' },
  rifiutato:           { label: 'Rifiutato',           bg: 'bg-red-500/10',    text: 'text-red-400',    border: 'border-red-500/20' },
  modifiche_richieste: { label: 'Modifiche richieste', bg: 'bg-orange-500/10', text: 'text-orange-400', border: 'border-orange-500/20' },
}

// Fetch an image URL and return base64 data URL
const toBase64 = async (url) => {
  const resp = await fetch(url)
  const blob = await resp.blob()
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

// ─── PDF Generation ───────────────────────────────────────────────────────────
const downloadPDF = async (preventivo, voci, fiscalProfile) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' })
  const W = 210
  const H = 297
  const mx = 20
  let y = 20

  const sortedVoci = [...voci].sort((a, b) => (a.ordine ?? 0) - (b.ordine ?? 0))
  const ragione = fiscalProfile?.ragione_sociale || 'Plutonic Studio'

  // ── Logo ─────────────────────────────────────────────────────────────────
  if (fiscalProfile?.logo_url) {
    try {
      const b64 = await toBase64(fiscalProfile.logo_url)
      const imgFmt = b64.startsWith('data:image/png') ? 'PNG' : 'JPEG'
      doc.addImage(b64, imgFmt, W - mx - 45, y, 45, 18, '', 'FAST')
    } catch (_) {}
  }

  // ── Company header ────────────────────────────────────────────────────────
  doc.setFontSize(18)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(20, 20, 20)
  doc.text(ragione, mx, y + 8)

  const companyLines = [
    fiscalProfile?.indirizzo,
    [fiscalProfile?.cap, fiscalProfile?.citta, fiscalProfile?.provincia].filter(Boolean).join(' '),
    fiscalProfile?.piva ? `P.IVA ${fiscalProfile.piva}` : null,
    fiscalProfile?.codice_fiscale ? `C.F. ${fiscalProfile.codice_fiscale}` : null,
  ].filter(Boolean)

  doc.setFontSize(8)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(140, 140, 140)
  companyLines.forEach((line, i) => {
    doc.text(line, mx, y + 14 + i * 4.5)
  })
  y += companyLines.length > 0 ? 14 + companyLines.length * 4.5 + 6 : 20

  // ── Divider ───────────────────────────────────────────────────────────────
  doc.setDrawColor(220, 220, 220)
  doc.setLineWidth(0.3)
  doc.line(mx, y, W - mx, y)
  y += 8

  // ── Preventivo title + status ─────────────────────────────────────────────
  doc.setFontSize(22)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(20, 20, 20)
  doc.text(`Preventivo #${String(preventivo.numero).padStart(4, '0')}`, mx, y)

  const statusLabel = STATUS_CFG[preventivo.status]?.label || preventivo.status
  doc.setFontSize(9)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(160, 160, 160)
  doc.text(statusLabel, W - mx, y, { align: 'right' })
  y += 6

  doc.setFontSize(11)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(60, 60, 60)
  doc.text(preventivo.titolo, mx, y)
  y += 5
  doc.setFontSize(9)
  doc.setTextColor(160, 160, 160)
  doc.text(fmtDate(preventivo.created_at), mx, y)
  y += 10

  // ── Info blocks (cliente, progetto, validità, termini) ───────────────────
  const infoBlocks = []
  if (preventivo.profiles) {
    infoBlocks.push({
      label: 'Cliente',
      value: preventivo.profiles.full_name || preventivo.profiles.nickname || '—',
    })
  }
  if (preventivo.progetti) {
    infoBlocks.push({ label: 'Progetto', value: preventivo.progetti.titolo })
  }
  infoBlocks.push({ label: "Validita'", value: `${preventivo.validita_giorni} giorni` })
  if (preventivo.termini_pagamento) {
    infoBlocks.push({ label: 'Termini di pagamento', value: preventivo.termini_pagamento })
  }

  if (infoBlocks.length > 0) {
    const cols = Math.min(infoBlocks.length, 3)
    const colW = (W - mx * 2) / cols
    infoBlocks.forEach((block, i) => {
      if (i > 0 && i % 3 === 0) y += 16
      const x = mx + (i % 3) * colW
      doc.setFontSize(7.5)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(150, 150, 150)
      doc.text(block.label.toUpperCase(), x, y)
      doc.setFontSize(10)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(30, 30, 30)
      doc.text(block.value, x, y + 5)
    })
    y += 16
  }

  // ── Line items table ──────────────────────────────────────────────────────
  // Header row
  doc.setFillColor(245, 245, 245)
  doc.rect(mx, y, W - mx * 2, 7, 'F')
  doc.setFontSize(7.5)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(130, 130, 130)
  doc.text('DESCRIZIONE', mx + 2, y + 4.5)
  doc.text("Q.TA'", W - mx - 54, y + 4.5, { align: 'right' })
  doc.text('PREZZO UNIT.', W - mx - 26, y + 4.5, { align: 'right' })
  doc.text('TOTALE', W - mx, y + 4.5, { align: 'right' })
  y += 7

  // Data rows
  sortedVoci.forEach((v, i) => {
    if (i % 2 === 1) {
      doc.setFillColor(252, 252, 252)
      doc.rect(mx, y, W - mx * 2, 8, 'F')
    }
    const tipo = v.tipo || 'flat'
    const isFlat = tipo === 'flat'
    const rowTotal = Number(v.quantita) * Number(v.prezzo_unitario)
    const desc = doc.splitTextToSize(v.descrizione, W - mx * 2 - 72)
    const rowH = Math.max(8, desc.length * 4.5 + 2)

    doc.setFontSize(9.5)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(30, 30, 30)
    doc.text(desc, mx + 2, y + 5)
    // qty column: hide for flat, show qty + unit label for ore/giorni
    if (!isFlat) {
      const tipoLabel = tipo === 'ore' ? 'h' : 'g'
      doc.setTextColor(120, 120, 120)
      doc.setFontSize(8)
      doc.text(`${v.quantita} ${tipoLabel}`, W - mx - 54, y + 5, { align: 'right' })
      doc.setFontSize(9.5)
      doc.setTextColor(30, 30, 30)
      doc.text(fmtPdf(v.prezzo_unitario), W - mx - 26, y + 5, { align: 'right' })
    }
    doc.setFont('helvetica', 'bold')
    doc.text(fmtPdf(rowTotal), W - mx, y + 5, { align: 'right' })

    y += rowH
    doc.setDrawColor(238, 238, 238)
    doc.setLineWidth(0.2)
    doc.line(mx, y, W - mx, y)

    if (y > H - 70) {
      doc.addPage()
      y = 20
    }
  })

  // ── Totals ────────────────────────────────────────────────────────────────
  y += 8
  const totale = sortedVoci.reduce((s, v) => s + Number(v.quantita) * Number(v.prezzo_unitario), 0)
  const ivaRate = fiscalProfile?.regime_fiscale === 'forfettario'
    ? 0
    : (Number(fiscalProfile?.iva_percentuale) || 22) / 100
  const ivaAmt = totale * ivaRate
  const ivaPercLabel = fiscalProfile?.regime_fiscale === 'forfettario'
    ? 'Operazione in franchigia IVA'
    : `IVA ${fiscalProfile?.iva_percentuale ?? 22}%`

  const totalsX = W - mx - 80

  doc.setFontSize(9.5)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(110, 110, 110)
  doc.text('Imponibile', totalsX, y)
  doc.text(fmtPdf(totale), W - mx, y, { align: 'right' })
  y += 6

  doc.text(ivaPercLabel, totalsX, y)
  if (fiscalProfile?.regime_fiscale !== 'forfettario') {
    doc.text(fmtPdf(ivaAmt), W - mx, y, { align: 'right' })
  }
  y += 4

  doc.setDrawColor(30, 30, 30)
  doc.setLineWidth(0.5)
  doc.line(totalsX, y, W - mx, y)
  y += 6

  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(20, 20, 20)
  doc.text('Totale', totalsX, y)
  doc.text(fmtPdf(totale + ivaAmt), W - mx, y, { align: 'right' })
  y += 8

  // ── Ritenuta d'acconto ────────────────────────────────────────────────────
  if (fiscalProfile?.ritenuta_acconto) {
    const ritenutaAmt = totale * 0.20
    const nettoDaRicevere = totale + ivaAmt - ritenutaAmt
    y += 4
    doc.setFontSize(9.5)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(110, 110, 110)
    doc.text("Ritenuta d'acconto 20%", totalsX, y)
    doc.text(`- ${fmtPdf(ritenutaAmt)}`, W - mx, y, { align: 'right' })
    y += 4
    doc.setDrawColor(30, 30, 30)
    doc.setLineWidth(0.5)
    doc.line(totalsX, y, W - mx, y)
    y += 6
    doc.setFontSize(11)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(20, 20, 20)
    doc.text('Netto da corrispondere', totalsX, y)
    doc.text(fmtPdf(nettoDaRicevere), W - mx, y, { align: 'right' })
    y += 8
    doc.setFontSize(7.5)
    doc.setFont('helvetica', 'italic')
    doc.setTextColor(150, 150, 150)
    doc.text(
      "Soggetta a ritenuta d'acconto del 20% ai sensi dell'art. 25 DPR 600/73",
      mx, y
    )
    y += 6
  }

  if (fiscalProfile?.regime_fiscale === 'forfettario') {
    doc.setFontSize(7.5)
    doc.setFont('helvetica', 'italic')
    doc.setTextColor(150, 150, 150)
    doc.text(
      "Operazione in franchigia da IVA ai sensi dell'art. 1 c.54-89 L. 190/2014",
      mx,
      y
    )
    y += 8
  }

  // ── Note ─────────────────────────────────────────────────────────────────
  if (preventivo.note) {
    y += 4
    doc.setFontSize(8)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(150, 150, 150)
    doc.text('NOTE', mx, y)
    y += 4
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9)
    doc.setTextColor(60, 60, 60)
    const noteLines = doc.splitTextToSize(preventivo.note, W - mx * 2)
    doc.text(noteLines, mx, y)
    y += noteLines.length * 4.5
  }

  // ── Footer ────────────────────────────────────────────────────────────────
  doc.setFontSize(7.5)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(190, 190, 190)
  doc.text(
    `${ragione}  ·  Documento generato il ${new Date().toLocaleDateString('it-IT')}`,
    W / 2,
    H - 10,
    { align: 'center' }
  )

  doc.save(
    `Preventivo_${String(preventivo.numero).padStart(4, '0')}_${preventivo.titolo.replace(/[^a-zA-Z0-9]/g, '_').slice(0, 30)}.pdf`
  )
}

// ─── VoceRow ───────────────────────────────────────────────────────────────────
const TIPO_OPTIONS = [
  { value: 'flat',    label: 'Flat' },
  { value: 'ore',     label: 'Ore' },
  { value: 'giorni',  label: 'Giorni' },
]

const VoceRow = ({ voce, onChange, onDelete, autoFocus }) => {
  const tipo = voce.tipo || 'flat'
  const isFlat = tipo === 'flat'
  return (
    <div className="grid grid-cols-12 gap-2 items-center">
      <input
        value={voce.descrizione}
        onChange={e => onChange({ ...voce, descrizione: e.target.value })}
        autoFocus={autoFocus}
        placeholder="Descrizione servizio..."
        className="col-span-5 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-violet-500 transition-colors"
      />
      <select
        value={tipo}
        onChange={e => onChange({ ...voce, tipo: e.target.value, quantita: e.target.value === 'flat' ? '1' : voce.quantita })}
        className="col-span-2 bg-gray-800 border border-gray-700 rounded-lg px-2 py-2 text-sm text-gray-300 focus:outline-none focus:border-violet-500 transition-colors"
      >
        {TIPO_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {isFlat ? (
        <div className="col-span-1 flex items-center justify-center text-gray-600 text-xs select-none">—</div>
      ) : (
        <input
          type="number"
          value={voce.quantita}
          onChange={e => onChange({ ...voce, quantita: e.target.value })}
          min="0.01"
          step="0.01"
          placeholder="1"
          className="col-span-1 bg-gray-800 border border-gray-700 rounded-lg px-2 py-2 text-sm text-white focus:outline-none focus:border-violet-500 transition-colors text-right"
        />
      )}
      <input
        type="number"
        value={voce.prezzo_unitario}
        onChange={e => onChange({ ...voce, prezzo_unitario: e.target.value })}
        min="0"
        step="0.01"
        placeholder={isFlat ? 'Totale' : 'Prezzo/u.'}
        className="col-span-3 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-violet-500 transition-colors text-right"
      />
      <button
        type="button"
        onClick={onDelete}
        className="col-span-1 flex items-center justify-center text-gray-600 hover:text-red-400 transition-colors"
      >
        <Trash2 size={14} />
      </button>
    </div>
  )
}

// ─── PreventivoModal ───────────────────────────────────────────────────────────
const PreventivoModal = ({ preventivo, onClose, onSaved, onDeleted, progetti, clienti, fiscalProfile }) => {
  const { profile } = useAuth()
  const isEdit = !!preventivo?.id

  const [titolo, setTitolo] = useState(preventivo?.titolo || '')
  const [projectId, setProjectId] = useState(preventivo?.project_id || '')
  const [clienteId, setClienteId] = useState(preventivo?.cliente_id || '')
  const [note, setNote] = useState(preventivo?.note || '')
  const [termini, setTermini] = useState(preventivo?.termini_pagamento || '')
  const [validita, setValidita] = useState(preventivo?.validita_giorni?.toString() || '30')
  const [voci, setVoci] = useState(preventivo?._voci || [{ id: `new-${Date.now()}`, descrizione: '', quantita: '1', prezzo_unitario: '', ordine: 0 }])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const newVoceRef = useRef(false)

  // AI assistant
  const [showAi, setShowAi] = useState(false)
  const [aiPrompt, setAiPrompt] = useState('')
  const [aiLoading, setAiLoading] = useState(false)
  const [aiError, setAiError] = useState(null)
  const [aiPreview, setAiPreview] = useState(null) // { voci, stima_giorni, note_stima }

  const handleAiGenerate = async () => {
    if (!aiPrompt.trim()) return
    setAiLoading(true); setAiError(null); setAiPreview(null)
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('preventivo-ai', {
        body: { prompt: aiPrompt.trim() },
      })
      if (fnErr || data?.error) throw new Error(data?.error || fnErr?.message || 'Errore AI')
      setAiPreview(data)
    } catch (e) {
      setAiError(e.message)
    }
    setAiLoading(false)
  }

  const handleAiAccept = () => {
    if (!aiPreview?.voci) return
    const newVoci = aiPreview.voci.map((v, i) => {
      const tipo = ['ore', 'giorni', 'flat'].includes(v.tipo) ? v.tipo : 'flat'
      const isFlat = tipo === 'flat'
      return {
        id: `ai-${Date.now()}-${i}`,
        descrizione: v.descrizione || '',
        tipo,
        quantita: isFlat ? '1' : String(v.quantita ?? 1),
        prezzo_unitario: String(v.prezzo_unitario ?? 0),
        ordine: i,
      }
    })
    setVoci(newVoci)
    if (aiPreview.note_stima && !note.trim()) setNote(aiPreview.note_stima)
    setShowAi(false); setAiPreview(null); setAiPrompt('')
  }

  // IVA from fiscal profile
  const ivaRate = fiscalProfile?.regime_fiscale === 'forfettario'
    ? 0
    : (Number(fiscalProfile?.iva_percentuale) || 22) / 100
  const ivaPercLabel = fiscalProfile?.regime_fiscale === 'forfettario'
    ? 'Franchigia IVA'
    : `IVA ${fiscalProfile?.iva_percentuale ?? 22}%`

  const addVoce = () => {
    newVoceRef.current = true
    setVoci(prev => [...prev, { id: `new-${Date.now()}`, descrizione: '', quantita: '1', prezzo_unitario: '', ordine: prev.length }])
  }

  const updateVoce = (idx, v) => setVoci(prev => prev.map((x, i) => i === idx ? v : x))
  const removeVoce = (idx) => setVoci(prev => prev.filter((_, i) => i !== idx))

  const totale = voci.reduce((s, v) => s + (parseFloat(v.quantita) || 0) * (parseFloat(v.prezzo_unitario) || 0), 0)
  const iva = totale * ivaRate

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!titolo.trim() || voci.length === 0) return
    setLoading(true)
    setError(null)

    const payload = {
      titolo: titolo.trim(),
      project_id: projectId || null,
      cliente_id: clienteId || null,
      note: note.trim() || null,
      termini_pagamento: termini.trim() || null,
      validita_giorni: parseInt(validita) || 30,
      updated_at: new Date().toISOString(),
    }

    let prevId
    if (isEdit) {
      const { error: e2 } = await supabase.from('preventivi').update(payload).eq('id', preventivo.id)
      if (e2) { setError(e2.message); setLoading(false); return }
      prevId = preventivo.id
      await supabase.from('preventivi_voci').delete().eq('preventivo_id', prevId)
    } else {
      const { data: d, error: e2 } = await supabase
        .from('preventivi')
        .insert({ ...payload, created_by: profile.id })
        .select()
        .single()
      if (e2) { setError(e2.message); setLoading(false); return }
      prevId = d.id
    }

    const vociPayload = voci
      .filter(v => v.descrizione.trim())
      .map((v, i) => {
        const tipo = v.tipo || 'flat'
        const isFlat = tipo === 'flat'
        return {
          preventivo_id: prevId,
          descrizione: v.descrizione.trim(),
          quantita: isFlat ? 1 : (parseFloat(v.quantita) || 1),
          prezzo_unitario: parseFloat(v.prezzo_unitario) || 0,
          tipo,
          ordine: i,
        }
      })
    if (vociPayload.length > 0) {
      await supabase.from('preventivi_voci').insert(vociPayload)
    }

    const { data: updated } = await supabase
      .from('preventivi')
      .select('*, progetti(titolo), profiles!preventivi_cliente_id_fkey(full_name, nickname), preventivi_voci(*)')
      .eq('id', prevId)
      .single()

    onSaved(updated, isEdit)
    onClose()
  }

  const handleDelete = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return }
    await supabase.from('preventivi').delete().eq('id', preventivo.id)
    onDeleted(preventivo.id)
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4 py-6">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800 flex-shrink-0">
          <h2 className="text-lg font-bold text-white">
            {isEdit ? `Modifica preventivo #${String(preventivo.numero).padStart(4,'0')}` : 'Nuovo preventivo'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors"><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Titolo *</label>
              <input
                value={titolo}
                onChange={e => setTitolo(e.target.value)}
                required
                autoFocus
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-violet-500 transition-colors"
                placeholder="Es. Produzione video aziendale..."
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Progetto</label>
                <select
                  value={projectId}
                  onChange={e => setProjectId(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-violet-500 transition-colors"
                >
                  <option value="">Nessuno</option>
                  {progetti.map(p => <option key={p.id} value={p.id}>{p.titolo}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Cliente</label>
                <select
                  value={clienteId}
                  onChange={e => setClienteId(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-violet-500 transition-colors"
                >
                  <option value="">Nessuno</option>
                  {clienti.map(c => <option key={c.id} value={c.id}>{c.full_name || c.nickname || c.id}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Validità (giorni)</label>
                <input
                  type="number"
                  value={validita}
                  onChange={e => setValidita(e.target.value)}
                  min="1"
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-violet-500 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Termini di pagamento</label>
                <input
                  value={termini}
                  onChange={e => setTermini(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-violet-500 transition-colors"
                  placeholder="Es. 50% acconto, 50% consegna"
                />
              </div>
            </div>

            {/* ── AI assistant panel ── */}
            <div className="rounded-xl border border-violet-500/30 bg-violet-500/5 overflow-hidden">
              <button
                type="button"
                onClick={() => { setShowAi(v => !v); setAiPreview(null); setAiError(null) }}
                className="w-full flex items-center gap-2 px-4 py-3 text-sm text-violet-300 hover:text-violet-200 transition-colors"
              >
                <Sparkles size={15} className="text-violet-400 flex-shrink-0" />
                <span className="font-medium">Genera preventivo con l'AI</span>
                <span className="ml-auto text-xs text-violet-500">{showAi ? '▲ chiudi' : '▼ apri'}</span>
              </button>

              {showAi && (
                <div className="px-4 pb-4 space-y-3 border-t border-violet-500/20">
                  <p className="text-xs text-gray-500 pt-3">
                    Descrivi il progetto in parole tue — l'AI genera le voci con i prezzi in base alle tue tariffe.
                  </p>
                  <textarea
                    value={aiPrompt}
                    onChange={e => setAiPrompt(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleAiGenerate() }}
                    rows={3}
                    placeholder="es. Video istituzionale per azienda tech: 1 giorno di riprese in sede, interviste a 3 persone, montaggio 3-4 min con animazioni grafiche e musica..."
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-violet-500 transition-colors resize-none"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={handleAiGenerate}
                    disabled={aiLoading || !aiPrompt.trim()}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white text-sm font-medium transition-colors"
                  >
                    <Sparkles size={14} />
                    {aiLoading ? 'Generazione in corso...' : 'Genera voci'}
                  </button>

                  {aiError && <p className="text-red-400 text-xs">{aiError}</p>}

                  {/* Preview risultato */}
                  {aiPreview && (
                    <div className="space-y-2">
                      <div className="bg-gray-800/80 rounded-xl divide-y divide-gray-700 overflow-hidden">
                        {aiPreview.voci.map((v, i) => (
                          <div key={i} className="flex items-center gap-3 px-3 py-2.5">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white truncate">{v.descrizione}</p>
                            </div>
                            <span className="text-xs text-gray-500 flex-shrink-0">×{v.quantita}</span>
                            <span className="text-sm font-medium text-violet-300 flex-shrink-0">
                              {fmt(v.quantita * v.prezzo_unitario)}
                            </span>
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-500 px-1">
                        <span>
                          Totale stimato: <strong className="text-white">{fmt(aiPreview.voci.reduce((s, v) => s + v.quantita * v.prezzo_unitario, 0))}</strong>
                          {aiPreview.stima_giorni ? ` · ${aiPreview.stima_giorni} giorni` : ''}
                        </span>
                      </div>
                      {aiPreview.note_stima && (
                        <p className="text-xs text-gray-500 italic px-1">{aiPreview.note_stima}</p>
                      )}
                      <button
                        type="button"
                        onClick={handleAiAccept}
                        className="w-full py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium transition-colors"
                      >
                        Usa queste voci →
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm text-gray-400">Voci *</label>
                <div className="grid grid-cols-12 gap-2 text-xs text-gray-600 pr-6 flex-1 ml-4">
                  <span className="col-span-5">Descrizione</span>
                  <span className="col-span-2 text-right">Tipo</span>
                  <span className="col-span-1 text-right">Q.tà</span>
                  <span className="col-span-3 text-right">€</span>
                </div>
              </div>
              <div className="space-y-2">
                {voci.map((v, i) => (
                  <VoceRow
                    key={v.id}
                    voce={v}
                    onChange={nv => updateVoce(i, nv)}
                    onDelete={() => removeVoce(i)}
                    autoFocus={newVoceRef.current && i === voci.length - 1}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={addVoce}
                className="mt-2 flex items-center gap-1.5 text-sm text-violet-400 hover:text-violet-300 transition-colors"
              >
                <Plus size={14} /> Aggiungi voce
              </button>
            </div>

            {totale > 0 && (
              <div className="bg-gray-800/50 rounded-xl px-4 py-3 space-y-1.5">
                <div className="flex justify-between text-sm text-gray-400">
                  <span>Imponibile</span><span>{fmt(totale)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-400">
                  <span>{ivaPercLabel}</span>
                  <span>{fiscalProfile?.regime_fiscale === 'forfettario' ? '—' : fmt(iva)}</span>
                </div>
                <div className="flex justify-between text-base font-bold text-white border-t border-gray-700 pt-1.5 mt-1">
                  <span>Totale</span><span>{fmt(totale + iva)}</span>
                </div>
                {fiscalProfile?.ritenuta_acconto && (() => {
                  const ritenutaAmt = (totale + iva) * 0.20
                  return (<>
                    <div className="flex justify-between text-sm text-gray-400">
                      <span>Ritenuta 20%</span>
                      <span className="text-red-400">- {fmt(ritenutaAmt)}</span>
                    </div>
                    <div className="flex justify-between text-sm font-bold text-green-400 border-t border-gray-700 pt-1.5 mt-0.5">
                      <span>Netto da ricevere</span><span>{fmt((totale + iva) - ritenutaAmt)}</span>
                    </div>
                  </>)
                })()}
              </div>
            )}

            <div>
              <label className="block text-sm text-gray-400 mb-1">Note</label>
              <textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                rows={3}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-violet-500 transition-colors resize-none text-sm"
                placeholder="Note legali, condizioni aggiuntive..."
              />
            </div>

            {error && <p className="text-red-400 text-sm">{error}</p>}
          </div>

          <div className="sticky bottom-0 bg-gray-900 border-t border-gray-800 px-6 py-4 flex gap-3">
            {isEdit && (
              <button
                type="button"
                onClick={handleDelete}
                className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  confirmDelete
                    ? 'bg-red-500/20 text-red-400 border border-red-500/40 hover:bg-red-500 hover:text-white'
                    : 'border border-gray-700 text-gray-500 hover:text-red-400 hover:border-red-500/40'
                }`}
              >
                {confirmDelete ? 'Conferma eliminazione' : 'Elimina'}
              </button>
            )}
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-lg border border-gray-700 text-gray-400 hover:text-white transition-colors text-sm">
              Annulla
            </button>
            <button
              type="submit"
              disabled={loading || !titolo.trim() || voci.filter(v => v.descrizione.trim()).length === 0}
              className="flex-1 py-2.5 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white font-medium text-sm transition-colors"
            >
              {loading ? 'Salvataggio...' : isEdit ? 'Salva' : 'Crea preventivo'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── DetailModal ───────────────────────────────────────────────────────────────
const DetailModal = ({ preventivo, onClose, onEdit, onStatusChange, fiscalProfile }) => {
  const voci = preventivo.preventivi_voci || []
  const totale = voci.reduce((s, v) => s + Number(v.quantita) * Number(v.prezzo_unitario), 0)
  const ivaRate = fiscalProfile?.regime_fiscale === 'forfettario'
    ? 0
    : (Number(fiscalProfile?.iva_percentuale) || 22) / 100
  const iva = totale * ivaRate
  const ivaPercLabel = fiscalProfile?.regime_fiscale === 'forfettario'
    ? 'Franchigia IVA'
    : `IVA ${fiscalProfile?.iva_percentuale ?? 22}%`

  const cfg = STATUS_CFG[preventivo.status] || STATUS_CFG.bozza
  const [showStatusMenu, setShowStatusMenu] = useState(false)
  const [downloading, setDownloading] = useState(false)

  const handleDownload = async () => {
    setDownloading(true)
    try {
      await downloadPDF(preventivo, voci, fiscalProfile)
    } catch (e) {
      console.error('PDF error', e)
    }
    setDownloading(false)
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4 py-6">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800 flex-shrink-0">
          <div className="flex items-center gap-3">
            <span className="text-gray-500 text-sm font-mono">#{String(preventivo.numero).padStart(4,'0')}</span>
            <h2 className="text-lg font-bold text-white truncate">{preventivo.titolo}</h2>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <button
                onClick={() => setShowStatusMenu(v => !v)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${cfg.bg} ${cfg.border} ${cfg.text}`}
              >
                {cfg.label} <ChevronDown size={12} />
              </button>
              {showStatusMenu && (
                <div className="absolute right-0 top-full mt-1 bg-gray-900 border border-gray-800 rounded-xl shadow-2xl z-10 overflow-hidden min-w-[180px]">
                  {Object.entries(STATUS_CFG).map(([key, c]) => (
                    <button
                      key={key}
                      onClick={() => { onStatusChange(key); setShowStatusMenu(false) }}
                      className={`w-full text-left px-4 py-2.5 text-sm font-medium transition-colors hover:bg-gray-800 ${c.text} ${preventivo.status === key ? 'bg-gray-800' : ''}`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={onEdit}
              className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
            >
              <Edit2 size={16} />
            </button>
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="p-2 rounded-lg text-gray-400 hover:text-violet-400 hover:bg-violet-500/10 transition-colors"
              title="Scarica PDF"
            >
              <Download size={16} className={downloading ? 'animate-pulse' : ''} />
            </button>
            <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors ml-1"><X size={20} /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div className="grid grid-cols-3 gap-4">
            {preventivo.progetti && (
              <div>
                <p className="text-xs text-gray-500 mb-1">Progetto</p>
                <p className="text-sm text-white font-medium">{preventivo.progetti.titolo}</p>
              </div>
            )}
            {preventivo.profiles && (
              <div>
                <p className="text-xs text-gray-500 mb-1">Cliente</p>
                <p className="text-sm text-white font-medium">
                  {preventivo.profiles.full_name || preventivo.profiles.nickname || '—'}
                </p>
              </div>
            )}
            <div>
              <p className="text-xs text-gray-500 mb-1">Validità</p>
              <p className="text-sm text-white font-medium">{preventivo.validita_giorni} giorni</p>
            </div>
            {preventivo.termini_pagamento && (
              <div className="col-span-3">
                <p className="text-xs text-gray-500 mb-1">Termini di pagamento</p>
                <p className="text-sm text-white">{preventivo.termini_pagamento}</p>
              </div>
            )}
          </div>

          <div>
            <div className="grid grid-cols-12 gap-2 text-xs text-gray-500 uppercase tracking-wider px-3 mb-2">
              <span className="col-span-6">Descrizione</span>
              <span className="col-span-2 text-right">Q.tà</span>
              <span className="col-span-2 text-right">€ Unit.</span>
              <span className="col-span-2 text-right">Totale</span>
            </div>
            <div className="bg-gray-800/40 rounded-xl divide-y divide-gray-800">
              {voci.sort((a, b) => a.ordine - b.ordine).map(v => (
                <div key={v.id} className="grid grid-cols-12 gap-2 px-3 py-3">
                  <span className="col-span-6 text-sm text-white">{v.descrizione}</span>
                  <span className="col-span-2 text-sm text-gray-400 text-right">{v.quantita}</span>
                  <span className="col-span-2 text-sm text-gray-400 text-right">{fmt(v.prezzo_unitario)}</span>
                  <span className="col-span-2 text-sm text-white font-medium text-right">{fmt(Number(v.quantita) * Number(v.prezzo_unitario))}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="ml-auto max-w-xs space-y-1.5">
            <div className="flex justify-between text-sm text-gray-400">
              <span>Imponibile</span><span>{fmt(totale)}</span>
            </div>
            <div className="flex justify-between text-sm text-gray-400">
              <span>{ivaPercLabel}</span>
              <span>{fiscalProfile?.regime_fiscale === 'forfettario' ? '—' : fmt(iva)}</span>
            </div>
            <div className="flex justify-between text-base font-bold text-white border-t border-gray-700 pt-2 mt-1">
              <span>Totale</span><span>{fmt(totale + iva)}</span>
            </div>
            {fiscalProfile?.ritenuta_acconto && (() => {
              const ritenutaAmt = (totale + iva) * 0.20
              return (<>
                <div className="flex justify-between text-sm text-gray-400 pt-1">
                  <span>Ritenuta d'acconto 20%</span>
                  <span className="text-red-400">- {fmt(ritenutaAmt)}</span>
                </div>
                <div className="flex justify-between text-base font-bold text-green-400 border-t border-gray-700 pt-2 mt-1">
                  <span>Netto da ricevere</span><span>{fmt((totale + iva) - ritenutaAmt)}</span>
                </div>
                <p className="text-xs text-gray-600 leading-relaxed">
                  Soggetta a ritenuta d'acconto del 20% ai sensi dell'art. 25 DPR 600/73
                </p>
              </>)
            })()}
            {fiscalProfile?.regime_fiscale === 'forfettario' && (
              <p className="text-xs text-gray-600 leading-relaxed">
                Operazione in franchigia da IVA ai sensi dell'art. 1 c.54-89 L. 190/2014
              </p>
            )}
          </div>

          {preventivo.note && (
            <div>
              <p className="text-xs text-gray-500 mb-2">Note</p>
              <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-line">{preventivo.note}</p>
            </div>
          )}

          {preventivo.messaggio_cliente && (
            <div className={`rounded-xl px-4 py-3 border ${
              preventivo.status === 'accettato' ? 'bg-green-500/10 border-green-500/20' :
              preventivo.status === 'rifiutato' ? 'bg-red-500/10 border-red-500/20' :
              'bg-orange-500/10 border-orange-500/20'
            }`}>
              <p className="text-xs text-gray-500 mb-1">Messaggio dal cliente</p>
              <p className="text-sm text-white">{preventivo.messaggio_cliente}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Preventivi ────────────────────────────────────────────────────────────────
const Preventivi = () => {
  const { user } = useAuth()
  const [preventivi, setPreventivi] = useState([])
  const [loading, setLoading] = useState(true)
  const [progetti, setProgetti] = useState([])
  const [clienti, setClienti] = useState([])
  const [fiscalProfile, setFiscalProfile] = useState(null)
  const [showCreate, setShowCreate] = useState(false)
  const [selected, setSelected] = useState(null)
  const [editing, setEditing] = useState(null)
  const [filtro, setFiltro] = useState('tutti')

  const fetchAll = useCallback(async () => {
    const { data } = await supabase
      .from('preventivi')
      .select('*, progetti(titolo), profiles!preventivi_cliente_id_fkey(full_name, nickname), preventivi_voci(*)')
      .order('created_at', { ascending: false })
    setPreventivi(data || [])
    setLoading(false)
  }, [])

  useEffect(() => {
    fetchAll()
    supabase.from('progetti').select('id, titolo').then(({ data }) => setProgetti(data || []))
    supabase.from('profiles').select('id, full_name, nickname').eq('role', 'client').then(({ data }) => setClienti(data || []))
  }, [fetchAll])

  // Load creator's fiscal profile
  useEffect(() => {
    if (!user?.id) return
    supabase
      .from('profiles')
      .select('ragione_sociale, piva, codice_fiscale, regime_fiscale, iva_percentuale, indirizzo, cap, citta, provincia, logo_url, ritenuta_acconto')
      .eq('id', user.id)
      .single()
      .then(({ data }) => setFiscalProfile(data))
  }, [user?.id])

  const handleSaved = (saved, isEdit) => {
    setPreventivi(prev =>
      isEdit
        ? prev.map(p => p.id === saved.id ? saved : p)
        : [saved, ...prev]
    )
  }
  const handleDeleted = (id) => {
    setPreventivi(prev => prev.filter(p => p.id !== id))
    setSelected(null)
  }

  const handleStatusChange = async (newStatus) => {
    if (!selected) return
    const { data } = await supabase
      .from('preventivi')
      .update({ status: newStatus, updated_at: new Date().toISOString() })
      .eq('id', selected.id)
      .select('*, progetti(titolo), profiles!preventivi_cliente_id_fkey(full_name, nickname), preventivi_voci(*)')
      .single()
    if (data) {
      setPreventivi(prev => prev.map(p => p.id === data.id ? data : p))
      setSelected(data)
    }
  }

  const filtrati = filtro === 'tutti' ? preventivi : preventivi.filter(p => p.status === filtro)
  const totalePerStatus = (s) => preventivi.filter(p => p.status === s).length

  // IVA for list preview
  const ivaRate = fiscalProfile?.regime_fiscale === 'forfettario'
    ? 0
    : (Number(fiscalProfile?.iva_percentuale) || 22) / 100

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
    </div>
  )

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Preventivi</h1>
          <p className="text-gray-500 text-sm mt-1">Gestione preventivi e contratti</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 bg-violet-600 hover:bg-violet-500 text-white font-medium px-4 py-2.5 rounded-lg transition-colors text-sm"
        >
          <Plus size={16} /> Nuovo preventivo
        </button>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        <button
          onClick={() => setFiltro('tutti')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${filtro === 'tutti' ? 'bg-violet-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}
        >
          Tutti ({preventivi.length})
        </button>
        {Object.entries(STATUS_CFG).map(([key, cfg]) => (
          totalePerStatus(key) > 0 && (
            <button
              key={key}
              onClick={() => setFiltro(key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${filtro === key ? `${cfg.bg} ${cfg.text} border ${cfg.border}` : 'bg-gray-800 text-gray-400 hover:text-white'}`}
            >
              {cfg.label} ({totalePerStatus(key)})
            </button>
          )
        ))}
      </div>

      {filtrati.length === 0 ? (
        <div className="text-center py-24">
          <FileText size={48} className="text-gray-700 mx-auto mb-4" />
          <p className="text-gray-600 mb-2">Nessun preventivo trovato</p>
          <button onClick={() => setShowCreate(true)} className="text-violet-400 hover:text-violet-300 text-sm transition-colors">
            Crea il primo preventivo →
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filtrati.map(p => {
            const cfg = STATUS_CFG[p.status] || STATUS_CFG.bozza
            const voci = p.preventivi_voci || []
            const totale = voci.reduce((s, v) => s + Number(v.quantita) * Number(v.prezzo_unitario), 0)
            return (
              <div
                key={p.id}
                onClick={() => setSelected(p)}
                className="bg-gray-900 border border-gray-800 rounded-2xl px-5 py-4 flex items-center gap-4 hover:border-gray-700 transition-colors cursor-pointer"
              >
                <div className="w-10 h-10 rounded-xl bg-gray-800 flex items-center justify-center flex-shrink-0">
                  <FileText size={18} className="text-gray-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 font-mono">#{String(p.numero).padStart(4,'0')}</span>
                    <p className="text-white font-medium text-sm truncate">{p.titolo}</p>
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    {p.progetti && <span className="text-xs text-gray-500">{p.progetti.titolo}</span>}
                    {p.profiles && <span className="text-xs text-gray-500">· {p.profiles.full_name || p.profiles.nickname}</span>}
                    <span className="text-xs text-gray-600">{new Date(p.created_at).toLocaleDateString('it-IT')}</span>
                  </div>
                </div>
                {totale > 0 && (
                  <span className="text-sm font-bold text-white flex-shrink-0">{fmt(totale * (1 + ivaRate))}</span>
                )}
                <span className={`flex-shrink-0 px-2.5 py-1 rounded-lg text-xs font-semibold border ${cfg.bg} ${cfg.border} ${cfg.text}`}>
                  {cfg.label}
                </span>
              </div>
            )
          })}
        </div>
      )}

      {showCreate && (
        <PreventivoModal
          preventivo={null}
          progetti={progetti}
          clienti={clienti}
          fiscalProfile={fiscalProfile}
          onClose={() => setShowCreate(false)}
          onSaved={(saved) => { handleSaved(saved, false); setShowCreate(false) }}
          onDeleted={handleDeleted}
        />
      )}

      {selected && !editing && (
        <DetailModal
          preventivo={selected}
          fiscalProfile={fiscalProfile}
          onClose={() => setSelected(null)}
          onEdit={() => { setEditing({ ...selected, _voci: selected.preventivi_voci || [] }); setSelected(null) }}
          onStatusChange={handleStatusChange}
        />
      )}

      {editing && (
        <PreventivoModal
          preventivo={editing}
          progetti={progetti}
          clienti={clienti}
          fiscalProfile={fiscalProfile}
          onClose={() => setEditing(null)}
          onSaved={(saved, isEdit) => { handleSaved(saved, isEdit); setEditing(null) }}
          onDeleted={handleDeleted}
        />
      )}
    </div>
  )
}

export default Preventivi
